export type PartnerLaunchEnv = 'live' | 'test';

export const PARTNER_EMBED_PATH = '/embed/ars';
export const PARTNER_LAUNCH_PATTERN = /^lc_(live|test)_[A-Za-z0-9_-]{43}$/;

export class MemoryStorage implements Storage {
  [name: string]: any;
  private readonly data = new Map<string, string>();

  get length(): number {
    return this.data.size;
  }

  clear(): void {
    this.data.clear();
  }

  getItem(key: string): string | null {
    const value = this.data.get(String(key));
    return value === undefined ? null : value;
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return index >= 0 && index < keys.length ? keys[index] : null;
  }

  removeItem(key: string): void {
    this.data.delete(String(key));
  }

  setItem(key: string, value: string): void {
    this.data.set(String(key), String(value));
  }
}

interface SandboxWindow {
  location: { pathname: string; search: string };
  history: { state: any; replaceState(data: any, unused: string, url?: string | null): void };
  document: any;
}

let launchCode: string | null = null;
let launchEnv: PartnerLaunchEnv | null = null;
let active = false;
let memoryStores: MemoryStorage[] = [];

export function readLaunchCode(location: { pathname: string; search: string }): string | null {
  const path = (location.pathname || '').replace(/\/+$/, '');
  if (path !== PARTNER_EMBED_PATH) return null;
  const code = new URLSearchParams(location.search || '').get('launch');
  return code && PARTNER_LAUNCH_PATTERN.test(code) ? code : null;
}

function define(target: any, property: string, descriptor: PropertyDescriptor): void {
  try {
    Object.defineProperty(target, property, { configurable: true, ...descriptor });
  } catch {}
}

export function installPartnerLaunchSandbox(win: SandboxWindow): boolean {
  const code = readLaunchCode(win.location);
  if (!code) return false;
  const local = new MemoryStorage();
  const session = new MemoryStorage();
  define(win, 'localStorage', { get: () => local });
  define(win, 'sessionStorage', { get: () => session });
  define(win, 'indexedDB', { get: () => undefined });
  define(win.document, 'cookie', { get: () => '', set: () => {} });
  try {
    win.history.replaceState(win.history.state, '', PARTNER_EMBED_PATH);
  } catch {}
  memoryStores = [local, session];
  launchCode = code;
  launchEnv = code.startsWith('lc_live_') ? 'live' : 'test';
  active = true;
  return true;
}

export function isPartnerLaunch(): boolean {
  return active;
}

export function partnerLaunchEnv(): PartnerLaunchEnv | null {
  return launchEnv;
}

export function takeLaunchCode(): string | null {
  const code = launchCode;
  launchCode = null;
  return code;
}

export function wipePartnerMemory(): void {
  memoryStores.forEach(store => store.clear());
}

export function resetPartnerLaunchState(): void {
  launchCode = null;
  launchEnv = null;
  active = false;
  memoryStores = [];
}

if (typeof window !== 'undefined') {
  installPartnerLaunchSandbox(window as unknown as SandboxWindow);
}
